import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { GraduationCap, Award, Star, Calendar } from 'lucide-react';
import { useStats } from '../../context/StatsContext';
import { userApi } from '../../config';

interface ApiResponse {
  success: boolean;
  data: {
    totalStudents: number;
    yearsOfExperience: number;
    totalInstructors: number;
    classesPerWeek: number;
  };
}

interface CounterProps {
  end: number;
  duration?: number;
  suffix?: string;
}

const Counter: React.FC<CounterProps> = ({ end, duration = 1800, suffix = '+' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-20px' });
  const animatedRef = useRef(false);

  useEffect(() => {
    if (!isInView || animatedRef.current || end <= 0) return;
    animatedRef.current = true;

    let startTimestamp: number | null = null;
    let animationFrameId: number;

    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      // Smooth ease-out cubic
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(ease * end));

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(step);
      } else {
        setCount(end);
      }
    };

    animationFrameId = requestAnimationFrame(step);

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [isInView, end, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {count}
      {suffix}
    </span>
  );
};

export default function StatsSection() {
  const { state, dispatch } = useStats();

  useEffect(() => {
    const fetchStats = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      try {
        const response = await userApi.get<ApiResponse>('/platform-stats');
        
        if (response.data.success && response.data.data) {
          dispatch({
            type: 'SET_STATS',
            payload: {
              totalStudents: response.data.data.totalStudents,
              yearsOfExperience: response.data.data.yearsOfExperience,
              totalInstructors: response.data.data.totalInstructors,
              classesPerWeek: response.data.data.classesPerWeek,
            },
          });
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: error instanceof Error ? error.message : 'Failed to fetch stats',
        });
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const stats = [
    {
      target: state.totalStudents || 172,
      label: 'HAPPY STUDENTS',
      icon: GraduationCap
    },
    {
      target: state.yearsOfExperience || 14,
      label: 'YEARS EXPERIENCE',
      icon: Award
    },
    {
      target: state.totalInstructors || 34,
      label: 'EXPERT INSTRUCTORS',
      icon: Star
    },
    {
      target: state.classesPerWeek || 13,
      label: 'CLASSES WEEKLY',
      icon: Calendar
    }
  ];

  return (
    <div className="bg-white pt-0 pb-4 sm:pb-6">
      <div className="max-w-[1320px] mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          className="rounded-2xl border border-[#E8E0D0] bg-white/85 backdrop-blur-sm p-6 sm:p-7 shadow-sm grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 items-center"
        >
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="flex items-center gap-3.5 sm:gap-4 justify-start sm:justify-center"
              >
                <div className="w-12 h-12 sm:w-13 sm:h-13 rounded-full bg-[#E5F3EE] text-[#2C6B56] flex items-center justify-center shrink-0 shadow-inner">
                  <IconComponent className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold font-serif text-[#15302B] leading-none mb-1">
                    <Counter end={stat.target} suffix="+" />
                  </div>
                  <div className="text-[10px] sm:text-[11px] font-bold tracking-[0.14em] uppercase text-[#7A8A82]">
                    {stat.label}
                  </div>
                </div>
              </div>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
}
